/* eslint-disable no-console */
import {CheckCircleOutlined, CloseOutlined} from '@ant-design/icons';
import {Drawer, Flex, Input} from 'antd';
import React, {useEffect, useRef, useState} from 'react';

import {ReactComponent as Copilot} from '../../assets/copliot.svg';
import {drawerZIndex} from '../../constants/Common';
import copilotChat, {
  type CopilotCitation
} from '../../services/modules/Home/copilotChat';
import useDataStore from '../../store/dataStore/dataStore';

import IntroText from './IntroText';
import ShowChat from './ShowChat';

const CopilotDrawer: React.FC<{open: boolean; onCancel: () => void}> = ({
  open,
  onCancel
}) => {
  const {TextArea} = Input;

  const [isEntered, setIsEntered] = useState<boolean>(false);
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
  const [isSend, setIsSend] = useState<boolean>(false);
  const [clickToTextArea, setClickToTextArea] = useState<any>({});
  const [dubChat, setDubChat] = useState<string>('');
  const {chatStore, setChatStore} = useDataStore.getState();
  const textAreaRef = useRef<any>(null);

  // const loremWords = [
  //   'lorem',
  //   'ipsum',
  //   'dolor',
  //   'sit',
  //   'amet',
  //   'consectetur',
  //   'adipiscing',
  //   'elit',
  //   'sed',
  //   'do',
  //   'eiusmod',
  //   'tempor',
  //   'incididunt',
  //   'ut',
  //   'labore',
  //   'et',
  //   'dolore',
  //   'magna',
  //   'aliqua'
  // ];

  // const generateLorem = (wordCount = 50) => {
  //   return Array.from(
  //     {length: wordCount},
  //     () => loremWords[Math.floor(Math.random() * loremWords.length)]
  //   ).join(' ');
  // };

  // useEffect(() => {
  //   let timeout: any;
  //   if (isSend) {
  //     setIsChatLoading(true);
  //     timeout = setTimeout(() => {
  //       setChatStore({type: 'receiver', value: generateLorem()});
  //       setIsChatLoading(false);
  //       setIsSend(false);
  //     }, 3000);
  //   }

  //   return () => {
  //     clearTimeout(timeout);
  //   };
  // }, [isSend]);

  const citationLink = (citation?: CopilotCitation) =>
    citation?.link ?? citation?.source_url ?? null;

  const citationLabel = (citation: CopilotCitation) =>
    (citation.label ?? citation.document_title ?? '').replace(/\s*-\s*$/, '');

  // `[n]` markers become links to the cited source; one without a link keeps
  // its label as a tooltip and stays readable in the Sources list below.
  const renderCitations = (text: string, citations: CopilotCitation[]) =>
    text.split(/(\[\d+\])/g).map((part, i) => {
      const marker = /^\[(\d+)\]$/.exec(part);

      if (!marker) {
        return part;
      }

      const citation = citations.find((item) => item.n === Number(marker[1]));
      const href = citationLink(citation);

      if (!citation) {
        return part;
      }

      return href ? (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noreferrer"
          title={citationLabel(citation)}
        >
          {part}
        </a>
      ) : (
        <span key={i} title={citationLabel(citation)}>
          {part}
        </span>
      );
    });

  // Bold text and citation markers, for a line of text or a table cell.
  const renderInline = (text: string, citations: CopilotCitation[]) =>
    text.split(/(\*\*.*?\*\*)/g).map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i}>{renderCitations(part.slice(2, -2), citations)}</strong>
        );
      }

      return (
        <React.Fragment key={i}>{renderCitations(part, citations)}</React.Fragment>
      );
    });

  const isTableRow = (line?: string) => line?.trim().startsWith('|') ?? false;
  const isTableSeparator = (line?: string) =>
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(line ?? '');
  const toCells = (line: string) =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((cell) => cell.trim());

  const parseText = (text: string, citations: CopilotCitation[] = []) => {
    // A markdown table is a `| a | b |` header row followed by `|---|---|`.
    const allLines = text.split('\n');
    const segments: Array<
      {type: 'text'; line: string} | {type: 'table'; rows: string[][]}
    > = [];

    for (let index = 0; index < allLines.length; index += 1) {
      const line = allLines[index];

      if (isTableRow(line) && isTableSeparator(allLines[index + 1])) {
        const rows = [toCells(line)];
        index += 2;
        while (index < allLines.length && isTableRow(allLines[index])) {
          rows.push(toCells(allLines[index]));
          index += 1;
        }
        index -= 1;
        segments.push({type: 'table', rows});
      } else {
        segments.push({type: 'text', line});
      }
    }

    const lines = segments.map((segment, index) => {
      if (segment.type === 'table') {
        const [header, ...body] = segment.rows;

        return (
          <div key={index} className="copilotTableWrapper">
            <table className="copilotTable">
              <thead>
                <tr>
                  {header.map((cell, i) => (
                    <th key={i}>{renderInline(cell, citations)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {header.map((_, i) => (
                      <td key={i}>{renderInline(row[i] ?? '', citations)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      // A table is a block, so no line break is needed next to one.
      return (
        <React.Fragment key={index}>
          {renderInline(segment.line, citations)}
          {segments[index + 1]?.type === 'text' && <br />}
        </React.Fragment>
      );
    });

    if (!citations.length) {
      return lines;
    }

    return (
      <>
        {lines}
        <br />
        <br />
        <strong>Sources</strong>
        {[...citations]
          .sort((a, b) => a.n - b.n)
          .map((citation) => {
            const href = citationLink(citation);
            const label = citationLabel(citation) || 'Source';

            return (
              <div key={citation.n}>
                [{citation.n}]{' '}
                {href ? (
                  <a href={href} target="_blank" rel="noreferrer">
                    {label}
                  </a>
                ) : (
                  label
                )}
              </div>
            );
          })}
      </>
    );
  };

  useEffect(() => {
    if (isSend) {
      setIsChatLoading(true);
      copilotChat(dubChat)
        .then((res) => {
          const answer = res?.answer_markdown?.trim() ?? '';

          if (answer) {
            setChatStore({
              type: 'receiver',
              value: parseText(answer, res?.citations ?? [])
            });
          } else {
            setChatStore({
              type: 'error',
              value: 'Oops! Something Went Wrong. Try Again'
            });
          }
          setDubChat('');
          setIsChatLoading(false);
          setIsSend(false);
        })
        .catch(() => {
          setChatStore({
            type: 'error',
            value: 'Oops! Something Went Wrong. Try Again'
          });
        })
        .finally(() => {
          setIsChatLoading(false);
          setIsSend(false);
        });
    }
  }, [isSend]);

  return (
    <Drawer
      placement={'right'}
      size="large"
      destroyOnClose={false}
      open={open}
      onClose={() => {
        onCancel();
        setIsEntered(false);
      }}
      className="copilotDrawer"
      style={{padding: '0px', background: '#F3F4F8'}}
      closeIcon={false}
      zIndex={drawerZIndex}
      title={
        <Flex
          style={{
            height: '50px',
            backgroundColor: '#719A5D',
            padding: '24px',
            flex: '0 0 7%'
          }}
          align="center"
          justify="space-between"
        >
          <Flex align="center" gap={10}>
            <div
              style={{
                backgroundColor: '#FFF3EA',
                borderRadius: '50%',
                height: '48px',
                width: '48px',
                alignContent: 'center',
                textAlign: 'center'
              }}
            >
              <Copilot />
            </div>
            <Flex
              style={{
                color: '#fff',
                fontSize: '24px',
                fontWeight: 700
              }}
            >
              Net Zero Copilot
            </Flex>
            <div
              style={{
                backgroundColor: '#FFF3EA',
                borderRadius: '4px',
                height: '22px',
                width: '36px',
                alignContent: 'center',
                textAlign: 'center',
                color: '#000',
                fontWeight: 500,
                fontSize: '12px'
              }}
            >
              Beta
            </div>
          </Flex>
          <CloseOutlined
            style={{
              fontSize: '16px',
              color: '#fff',
              cursor: 'pointer'
            }}
            onClick={() => {
              onCancel();
              setIsEntered(false);
            }}
          />
        </Flex>
      }
      footer={
        <Flex style={{width: '100%'}} align="center" justify="space-between">
          <TextArea
            autoSize={{minRows: 2, maxRows: 6}}
            // style={{height: '100px', margin: '24px'}}
            placeholder="Ask me anything..."
            style={{width: '100%', margin: '10px'}}
            value={clickToTextArea?.value ?? ''}
            ref={textAreaRef}
            onKeyDown={(event) => {
              if (
                event.key === 'Enter' &&
                !event.ctrlKey &&
                !event.altKey &&
                !event.shiftKey &&
                !event.metaKey
              ) {
                if (
                  !clickToTextArea?.value ||
                  clickToTextArea?.value?.trim().length === 0
                )
                  return;
                event.preventDefault();
                setChatStore(clickToTextArea);
                setDubChat(clickToTextArea?.value);
                setClickToTextArea({});
                setIsEntered(true);
                setIsSend(true);
              }
            }}
            onChange={(e) => {
              setClickToTextArea({value: e.target.value, type: 'send'});
            }}
          />
          <CheckCircleOutlined
            style={{
              fontSize: '32px',
              height: '29px',
              cursor:
                clickToTextArea?.value?.trim().length > 0
                  ? 'pointer'
                  : 'not-allowed',
              backgroundColor: '#719A5D',
              borderRadius: '50%',
              // alignSelf: 'flex-end',
              color: '#fff',
              marginRight: '10px',
              opacity: clickToTextArea?.value?.trim().length > 0 ? 1 : 0.5
              // position: 'absolute',
              // bottom: '20%',
              // right: '4%',
            }}
            onClick={() => {
              if (
                !clickToTextArea?.value ||
                clickToTextArea?.value?.trim().length === 0
              )
                return;
              setChatStore(clickToTextArea);
              setDubChat(clickToTextArea?.value);
              setClickToTextArea({});
              setIsEntered(true);
              setIsSend(true);
            }}
          />
        </Flex>
      }
    >
      <Flex style={{height: '100%', width: '100%'}} vertical>
        <Flex
          style={{flex: '1 1 65%', backgroundColor: '#fff', width: '100%'}}
          justify={isEntered ? 'end' : 'center'}
        >
          {isEntered ? (
            <ShowChat allChat={chatStore} isChatLoading={isChatLoading} />
          ) : (
            <IntroText
              setClickToTextArea={setClickToTextArea}
              textRef={textAreaRef}
            />
          )}
        </Flex>
      </Flex>
    </Drawer>
  );
};

export default CopilotDrawer;
